// ─────────────────────────────────────────────────────────────────────────────
// DocumentsLibrary — standalone /documents page.
//
// Lists every drawing uploaded across all jobs, with client-side filters by
// job name, PMK, revision, doc_type, and approval status. Uses the shared
// DrawingsTable component for rendering.
//
// Backed by GET /api/document (see outputs/SFM-drawings-backend-spec.md).
// Defensive — renders a clear "backend not yet live" state when the endpoint
// is absent, so the page doesn't silently white-out.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useMemo, useState } from "react";
import PageGuide from "../../components/PageGuide";
import { Helmet } from "react-helmet-async";
import {
  Container, Typography, TextField, MenuItem, Stack, Button,
  Breadcrumbs, Paper, Box, Snackbar, Alert,
} from "@mui/material";
import { HomeRounded } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import DrawingsTable from "../../components/JobDrawing/DrawingsTable";
import DeleteDocumentDialog from "../../components/JobDrawing/DeleteDocumentDialog";
import EorStampDialog from "../../components/JobDrawing/EorStampDialog";
import LifecycleTransitionDialog from "../../components/LifecycleStatus/LifecycleTransitionDialog";
import { listByJob, deleteDocument, restoreDocument } from "../../utils/documentsApi";
import ApiCall from "../../utils/apicall";
import prettifyDocumentName from "../../utils/prettifyDocumentName";

export default function DocumentsLibrary() {
  const navigate = useNavigate();

  // Filter state
  // BUG FIX 2026-05-02 (CR-2026-FE-DOCUMENTS-LIBRARY-FETCH): default
  // statusFilter changed from "approved" to "all" because production has 0
  // approved docs today (everything is `pending` or `legacy_imported`). With
  // the old default an auditor or user opening the page saw nothing.
  const [jobFilter, setJobFilter] = useState("all");
  const [pmkFilter, setPmkFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [docTypeFilter, setDocTypeFilter] = useState("all");
  // GAP-AISC207-010 closure (CR-2026-AISC-DOC-EXTERNAL-ORIGIN-FLAG, P-class)
  // AISC 207-25 §2.8.5 — external-origin (customer-supplied) vs internally-detailed
  const [originFilter, setOriginFilter] = useState("all"); // "all" | "external" | "internal"

  // Fetched data
  const [allDocs, setAllDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notAvailable, setNotAvailable] = useState(false);
  const [error, setError] = useState(null);
  const [jobs, setJobs] = useState([]);

  // Delete dialog state
  const [deleteDoc, setDeleteDoc] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [toast, setToast] = useState(null); // { message, severity }
  const [refreshKey, setRefreshKey] = useState(0);
  const [eorDoc, setEorDoc] = useState(null);
  const [lifecycleDoc, setLifecycleDoc] = useState(null);

  // Load jobs for the filter dropdown (best effort — read-only)
  useEffect(() => {
    ApiCall.get("/job")
      .then((res) => {
        const arr =
          res?.data?.data?.jobs ||
          res?.data?.jobs ||
          (Array.isArray(res?.data) ? res.data : []) ||
          [];
        setJobs(arr);
      })
      .catch(() => { setJobs([]); });
  }, []);

  // Load docs — either all, or by single job. refreshKey re-fires on delete.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setNotAvailable(false);

    const job = jobFilter === "all" ? null : jobFilter;
    listByJob(job, { latestOnly: false, approvalStatus: statusFilter })
      .then((r) => {
        if (cancelled) return;
        setAllDocs(r.documents || []);
        setNotAvailable(!!r.notAvailable);
        setError(r.error || null);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [jobFilter, statusFilter, refreshKey]);

  // ── Delete flow ───────────────────────────────────────────────────────────
  const handleDeleteClick = (doc) => {
    setDeleteError(null);
    setDeleteDoc(doc);
  };

  const handleDeleteConfirm = async (reason) => {
    if (!deleteDoc) return;
    setDeleting(true);
    setDeleteError(null);
    const result = await deleteDocument(deleteDoc.id, reason);
    setDeleting(false);
    if (result.ok) {
      const fileName = prettifyDocumentName(deleteDoc);
      setDeleteDoc(null);
      setToast({ message: `Archived "${fileName}". Recoverable from the Archived filter.`, severity: "success" });
      setRefreshKey((k) => k + 1);
    } else {
      const status = result.status;
      let msg = result.error?.response?.data?.error
            || result.error?.response?.data?.message
            || result.error?.message
            || "Delete failed";
      if (status === 409 || status === 403) {
        // Production live droplet returns 409 (Conflict) per AISC 303 §4
        // records preservation. 403 supported for back-compat in case any
        // intermediate proxy layer remaps the status.
        msg = "Server refused: this document is approved and cannot be deleted (records preservation per AISC 303 §4 / ISO 9001 §7.5.3).";
      } else if (status === 404) {
        msg = "Document not found (already deleted?).";
      } else if (status === 401) {
        msg = "Not authorized — please re-login and try again.";
      }
      setDeleteError(new Error(msg));
    }
  };

  const handleDeleteClose = () => {
    if (deleting) return;
    setDeleteDoc(null);
    setDeleteError(null);
  };

  // ── Restore flow (CR-2026-DOCUMENTS-ARCHIVE-RESTORE 2026-05-05) ──────────
  // Inline confirm — no separate dialog because restore is the safe direction.
  // The row goes from archived back to its prior approval_status. Audit_log
  // wiring is a future AP-class CR.
  const handleRestoreClick = async (doc) => {
    if (!doc) return;
    const fileName = prettifyDocumentName(doc);
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Restore "${fileName}"? It will return to the active list.`)) {
      return;
    }
    const result = await restoreDocument(doc.id);
    if (result.ok) {
      setToast({ message: `Restored "${fileName}".`, severity: "success" });
      setRefreshKey((k) => k + 1);
    } else {
      const status = result.status;
      let msg = result.error?.response?.data?.message
            || result.error?.response?.data?.error
            || result.error?.message
            || "Restore failed";
      if (status === 409) {
        msg = "Document is not currently archived; nothing to restore.";
      } else if (status === 404) {
        msg = "Document not found.";
      } else if (status === 401) {
        msg = "Not authorized — please re-login and try again.";
      }
      setToast({ message: msg, severity: "error" });
    }
  };

  // Build a job-id → job-name map from the loaded jobs list so we can
  // enrich documents client-side. The backend /api/document endpoint does
  // not include the job name in the document payload (no JOIN), so without
  // this enrichment the table renders the raw integer job_id. Fix
  // 2026-05-05 (CR-2026-FE-DOCUMENTS-JOB-NAME-ENRICH).
  const jobNameById = useMemo(() => {
    const m = new Map();
    (jobs || []).forEach((j) => {
      if (j && j.id != null) {
        m.set(Number(j.id), j.name || j.job_name || `Job #${j.id}`);
      }
    });
    return m;
  }, [jobs]);

  // Apply client-side filters that the backend query doesn't cover, AND
  // enrich each document with `job_name` derived from jobNameById so the
  // table's Job column shows a human-readable name instead of an integer.
  const filtered = useMemo(() => {
    let out = allDocs.map((d) => {
      // Don't overwrite a backend-provided job_name; only fill the gap.
      if (d.job_name) return d;
      const name = d.job_id != null ? jobNameById.get(Number(d.job_id)) : null;
      return name ? { ...d, job_name: name } : d;
    });
    if (pmkFilter.trim()) {
      const q = pmkFilter.trim().toLowerCase();
      out = out.filter((d) => (d.pmk_number || d.pmk || "").toLowerCase().includes(q));
    }
    if (docTypeFilter !== "all") {
      out = out.filter((d) => (d.doc_type || "") === docTypeFilter);
    }
    // GAP-AISC207-010 — AISC §2.8.5 external-origin filter
    if (originFilter === "external") {
      out = out.filter((d) => !!d.is_external_origin);
    } else if (originFilter === "internal") {
      out = out.filter((d) => !d.is_external_origin);
    }
    return out;
  }, [allDocs, jobNameById, pmkFilter, docTypeFilter, originFilter]);

  const docTypes = useMemo(() => {
    const s = new Set(["all"]);
    allDocs.forEach((d) => d.doc_type && s.add(d.doc_type));
    return Array.from(s);
  }, [allDocs]);

  return (
    <>
      <Helmet><title>Documents Library | SFM</title></Helmet>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Button startIcon={<HomeRounded />} onClick={() => navigate("/")} size="small">Home</Button>
          <Typography color="text.primary">Documents Library</Typography>
        </Breadcrumbs>

        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Drawings &amp; Documents
          </Typography>
          <Button variant="contained" onClick={() => navigate("/documents/upload")}>
            Upload
          </Button>
        </Stack>
        <PageGuide
          why="Keep every drawing and document controlled in one place — current revisions, with old ones retained, not deleted."
          who="Anyone issuing or using controlled drawings; the QM controlling revisions."
          what="Your library of drawings, mill certs, procedures, and other controlled documents."
          how="Upload a document, tag it to a job and type; approved drawings stay on the record."
          when="Whenever a drawing or document is issued, revised, or received."
          standard="AISC 303 §4 · ISO 9001:2015 §7.5.3"
        />

        {/* Filter bar */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              select
              size="small"
              label="Job"
              value={jobFilter}
              onChange={(e) => setJobFilter(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="all">All jobs</MenuItem>
              {jobs.map((j) => (
                <MenuItem key={j.id} value={String(j.id)}>
                  {j.name || `Job #${j.id}`}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              size="small"
              label="PMK contains…"
              placeholder="e.g. 5253c1"
              value={pmkFilter}
              onChange={(e) => setPmkFilter(e.target.value)}
              sx={{ minWidth: 200 }}
            />

            <TextField
              select
              size="small"
              label="Approval status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="superseded">Superseded</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
              <MenuItem value="all">All</MenuItem>
            </TextField>

            <TextField
              select
              size="small"
              label="Type"
              value={docTypeFilter}
              onChange={(e) => setDocTypeFilter(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              {docTypes.map((t) => (
                <MenuItem key={t} value={t}>{t === "all" ? "All types" : t}</MenuItem>
              ))}
            </TextField>

            {/* GAP-AISC207-010 — AISC §2.8.5 external-origin filter */}
            <TextField
              select
              size="small"
              label="Origin"
              value={originFilter}
              onChange={(e) => setOriginFilter(e.target.value)}
              sx={{ minWidth: 160 }}
              helperText="AISC §2.8.5"
            >
              <MenuItem value="all">All origins</MenuItem>
              <MenuItem value="external">External (customer-supplied)</MenuItem>
              <MenuItem value="internal">Internal (in-house)</MenuItem>
            </TextField>
          </Stack>
        </Paper>

        {/* Result count + dedup disclosure (CR-2026-D11 — D-11 closure;
            Codex F5 fix 2026-05-07 — copy now matches the actual fetch
            shape, which is latestOnly:false. Showing all revisions is
            the audit-correct default — drawing reviewers need to see
            superseded revisions too, not just the latest. The previous
            copy claimed each row was the latest revision per (Job, PMK)
            and contradicted the data; auditor walking the page would
            have caught the mismatch). */}
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {loading
              ? "Loading…"
              : (
                <>
                  Showing {filtered.length} drawing{filtered.length === 1 ? "" : "s"}
                  {allDocs.length !== filtered.length && ` (filtered from ${allDocs.length})`}
                  . <span style={{ opacity: 0.75 }}>
                    All revisions are listed (one row per revision); the supersede chain is preserved for audit. Use the Status column to identify the active revision per (Job, PMK) — superseded revisions show as <em>superseded</em>.
                    Per AISC 303 §4.4 and ISO 9001 §7.5.3, all prior revisions are retained in the documents table and the audit_log captures every revision transition.
                  </span>
                </>
              )}
          </Typography>
        </Box>

        <DrawingsTable
          documents={filtered}
          loading={loading}
          notAvailable={notAvailable}
          error={error}
          emptyMessage={
            statusFilter === "archived"
              ? "No archived documents in the current scope. Archive a document to see it here."
              : "No drawings match the current filters."
          }
          showJob
          showPmk
          showFabItem
          onDelete={handleDeleteClick}
          onRestore={handleRestoreClick}
          onRecordEorStamp={(doc) => setEorDoc(doc)}
          onManageLifecycle={(doc) => setLifecycleDoc(doc)}
        />

        <DeleteDocumentDialog
          open={!!deleteDoc}
          document={deleteDoc}
          onClose={handleDeleteClose}
          onConfirm={handleDeleteConfirm}
          busy={deleting}
          error={deleteError}
        />

        <EorStampDialog
          doc={eorDoc}
          open={!!eorDoc}
          onClose={() => setEorDoc(null)}
          onSuccess={(res) => {
            setEorDoc(null);
            setToast({ message: `EOR stamp recorded \u2014 ${(res && res.data && res.data.lifecycle_status) || "approved"}.`, severity: "success" });
            setRefreshKey((k) => k + 1);
          }}
        />

        <LifecycleTransitionDialog
          open={!!lifecycleDoc}
          documentId={lifecycleDoc?.id}
          onClose={() => setLifecycleDoc(null)}
          onTransitioned={() => {
            setLifecycleDoc(null);
            setToast({ message: "Document lifecycle updated.", severity: "success" });
            setRefreshKey((k) => k + 1);
          }}
        />

        <Snackbar
          open={!!toast}
          autoHideDuration={5000}
          onClose={() => setToast(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          {toast ? (
            <Alert
              onClose={() => setToast(null)}
              severity={toast.severity}
              sx={{ width: "100%" }}
            >
              {toast.message}
            </Alert>
          ) : null}
        </Snackbar>
      </Container>
    </>
  );
}
